/**
 * Sidebar Slice
 * 
 * Manages sidebar state (replaces SidebarProvider context).
 * 
 * Key improvements over SidebarProvider:
 * - Stable action references (no new function every render)
 * - Selective subscriptions via Zustand
 * - No provider needed
 * - Shared state across entire app
 */

import type { StateCreator } from 'zustand';
import type { BoundStoreState, SidebarSlice, SidebarState } from '../types';

// ============================================================================
// Initial State
// ============================================================================

export const sidebarSliceInitialState = {
  sidebarState: 'expanded' as SidebarState,
  isMobile: false,
};

// ============================================================================
// Slice Creator
// ============================================================================

/**
 * Creates the sidebar slice.
 */
export const createSidebarSlice: StateCreator<
  BoundStoreState,
  [['zustand/devtools', never]],
  [],
  SidebarSlice
> = (set, get) => ({
  // State
  ...sidebarSliceInitialState,

  // Actions
  toggleSidebar: () =>
    set(
      (state) => ({
        sidebarState: state.sidebarState === 'expanded' ? 'collapsed' : 'expanded',
      }),
      undefined,
      'sidebar/toggle'
    ),

  setSidebarState: (sidebarState: SidebarState) =>
    set(
      { sidebarState },
      undefined,
      'sidebar/setState'
    ),

  setIsMobile: (isMobile: boolean) => {
    const current = get();
    // On mobile, default to collapsed
    const newState = isMobile ? 'collapsed' : current.sidebarState;
    set(
      { isMobile, sidebarState: newState },
      undefined,
      'sidebar/setMobile'
    );
  },
});

// ============================================================================
// Persistence Configuration
// ============================================================================

/**
 * Keys to persist from the sidebar slice.
 * Don't persist isMobile as it's detected at runtime.
 */
export const sidebarSlicePersistKeys: (keyof SidebarSlice)[] = [
  'sidebarState',
];
